import { scaleLinear } from 'd3-scale';
import { blackBase, contrast, whiteBase } from '@mentimeter/ragnar-colors';
import type { Accessor, Visualization } from '../types';
import { SvgWithFont } from '../utils/SvgWithFont';
import { Pin } from './Pin';
import { PlaceholderImage } from './PlaceholderImage';
import { CorrectAreaOverlay } from './CorrectAreaOverlay';

interface PinData {
  id: string;
  numberOfPins: number;
  position: {
    percentageX: number;
    percentageY: number;
  };
}

interface CorrectArea {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

function hasUrl(
  image: { url: string } | { placeholderImageColor: string },
): image is { url: string } {
  return 'url' in image;
}

export const PinOnImage = ({
  width,
  height,
  fontFamily,
  image,
  pinColor,
  data,
  correctArea,
  disableAnimations,
  keyAccessor = (d: PinData) => d.id.toString(),
  labelAccessor = (d: PinData) =>
    d.numberOfPins === 1 ? '' : d.numberOfPins.toString(),
  valueAccessor = (d: PinData) => d.position,
}: Omit<
  Visualization<PinData, never, PinData['position']>,
  'staticOptions' | 'textColor' | 'colors' | 'labelAccessor'
> & {
  image: { url: string } | { placeholderImageColor: string };
  pinColor: string;
  labelAccessor?: Accessor<PinData, string>;
  correctArea:
    | { type: 'disabled' }
    | { type: 'enabled-hidden'; correctArea: CorrectArea }
    | { type: 'enabled-visible'; correctArea: CorrectArea };
}) => {
  const scaleX = scaleLinear().domain([0, 1]).range([0, width]);
  const scaleY = scaleLinear().domain([0, 1]).range([0, height]);

  return (
    <SvgWithFont
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid"
      fontFamily={fontFamily}
      style={{ overflow: 'visible' }}
    >
      {hasUrl(image) ? (
        <image href={image.url} width={width} height={height} />
      ) : (
        <PlaceholderImage color={image.placeholderImageColor} />
      )}
      {correctArea.type !== 'disabled' && (
        <CorrectAreaOverlay
          isVisible={correctArea.type === 'enabled-visible'}
          width={width}
          height={height}
          x={scaleX(correctArea.correctArea.left)}
          y={scaleY(correctArea.correctArea.top)}
          correctAreaWidth={
            scaleX(correctArea.correctArea.right) -
            scaleX(correctArea.correctArea.left)
          }
          correctAreaHeight={
            scaleY(correctArea.correctArea.bottom) -
            scaleY(correctArea.correctArea.top)
          }
          disableAnimations={disableAnimations}
        />
      )}
      {data.map((d) => {
        const x = scaleX(valueAccessor(d).percentageX);
        const y = scaleY(valueAccessor(d).percentageY);
        const label = labelAccessor(d);
        const key = keyAccessor(d);
        const radius = 13 * Math.log10(15 * d.numberOfPins);

        const contrastToWhite = contrast(pinColor, whiteBase);
        const textColor = contrastToWhite > 4.5 ? whiteBase : blackBase;

        return (
          <Pin
            key={key}
            x={x}
            y={y}
            label={label}
            radius={radius}
            color={pinColor}
            textColor={textColor}
            disableAnimations={disableAnimations}
          />
        );
      })}
    </SvgWithFont>
  );
};
